/**
 * @ngdoc service
 * @name app.navigationService
 * @description
 * # Navigation Service.
 * Manages key navigation across components using a pubsub for messaging.
 *
 * When to use the navigation service:
 * - When the component has focus and is ready to lose focus.
 * - If the component hosts multiple `.focusable` elements, it will needs to handle its internal navigation,
 *   and only publish key events to notify the navigation service when it has reached the first / last row or column
 *   and it ready to loose focus.
 *
 * There are 3 steps to incorporate navigation across components:
 * - Use the css classes `.focusable` and the extra `.focusable-first`, `.focusable-last` elements
 *   to allow the service to find elements to focus on within specific containers
 * - Define cross component navigation behavior in navigation.js service, in `processKeyAction()` function.
 * - New components should handle navigation internally and only publish events when ready to loose focus.
 *   Message structure is: 'navigation.key.{name}' where {name} is the key name.
 *
 * ## Example
 * ``` js
 *      onKeyDown(e) {
 *        switch (true) {
 *          case keyboard.isEnter(e):
 *            // perform some internal action
 *            break;
 *          case keyboard.isUp(e):
 *            PubSub.publish('navigation.key.up', 'nameOfSourceComponent');
 *            break;
 *          case keyboard.isDown(e):
 *            PubSub.publish('navigation.key.down', 'nameOfSourceComponent');
 *            break;
 *        }
 *      }
 * ```
 * @requires ng:$document
 * @requires ng:$state
 * @requires ng:$timeout
 * @requires constants.topics
 * @requires constants.states
 **/
/*---------------------------------------------------------*\
 * Imports
\*---------------------------------------------------------*/
import angular from 'angular';
import PubSub from 'pubsub-js';
import _ from 'lodash';
/*---------------------------------------------------------*\
 * WeakMap
\*---------------------------------------------------------*/
const deps = {};

const KEY_UP = 'up';
const KEY_DOWN = 'down';
const KEY_LEFT = 'left';
const KEY_RIGHT = 'right';
// const KEY_BACK = 'back';
const KEY_ENTER = 'enter';

class NavigationService {
  //-----------------------------------
  // Constructor
  //-----------------------------------
  constructor($document, $state, $timeout, topics, states) {
    deps.$document = $document;
    deps.$state = $state;
    deps.$timeout = $timeout;
    deps.topics = topics;
    deps.states = states;

    this.digestCounter = 0;
    PubSub.subscribe(deps.topics.nav.allKeys, this.onKeyMessage.bind(this));
    PubSub.subscribe(deps.topics.headerMain.selectTab, this.onTabMessage.bind(this));
  }

  //-----------------------------------
  // Events
  //-----------------------------------
  /**
   * @private
   */

  /**
   * @ngdoc
   * @name app.navigationService#onKeyMessage
   * @methodOf app.navigationService
   * @example
   * ``` js
   * onKeyMessage(msg, data);
   * PubSub.subscribe(deps.topics.nav.allKeys, this.onKeyMessage.bind(this));
   * ```
   * @param {string} msg The string used for communicating on PubSub.
   * @param {object} data The data being passed on the PubSub.
   * @description
   * Private service method to manage the flow of data for navigation pubsub events.
   * if data is passed in, it will generate a source from that data and it will call
   * {@link app.navigationService#processKeyAction processKeyAction} method.
   * @returns {boolean} it will allways return true.
   **/
  onKeyMessage(msg, data) {
    if (!data) {
      return true;
    }
    const source = typeof data === 'string' ? data : data.source;
    const key = msg.substr(deps.topics.nav.allKeys.length + 1);
    this.processKeyAction(source, key, data);
    return true;
  }

  /**
   * @ngdoc
   * @name app.navigationService#onTabMessage
   * @methodOf app.navigationService
   * @example
   * ``` js
   * onTabMessage('nav-message', {item{state: 'home'}});
   * PubSub.subscribe(deps.topics.nav.allKeys, this.onTabMessage.bind(this));
   * ```
   * @param {string} msg The string used for communicating on PubSub.
   * @param {object} data The data being passed on the PubSub.
   * @description
   * Private service method to manage $state changes for navigation PubSub events.
   * It will check the data param for the presence of 'data.item.state', if it exists it will call $state.go with it
   * as a param. if it does not exist, it will not do anything.
   **/
  onTabMessage(msg, data) {
    // if item doesnt have state don't go anywhere. This is used for traffic and weather tabs.
    if (data.item.state) {
      deps.$state.go(data.item.state);
    }
  }

  //-----------------------------------
  // Methods
  //-----------------------------------
  /**
   * @ngdoc
   * @name app.navigationService#getFocusableEl
   * @methodOf app.navigationService
   * @example
   * ``` js
   * navigationService.getFocusableEl('.class-name');
   * navigationService.getFocusableEl('.class-name', 'first');
   * navigationService.getFocusableEl('.class-name', 5);
   * ```
   * @param {string} inParentNode parent element containing the 'focusable' elements.
   * @param {(string|number)=} index  The position of the element. Values: first, last. Defaults to 'first' if no
   * index is passed.
   * @description
   * Service method to manage focus navigation.
   * @returns {element} element where focus will be added;
   **/
  getFocusableEl(inParentNode, index) {
    const posClasses = {
      first: '.focusable-first',
      last: '.focusable-last'
    };
    if (typeof index === 'number') {
      return deps.$document[0].querySelectorAll(inParentNode + ' .focusable')[index];
    }
    const pos = posClasses[index || 'first'];
    // .focusable-first may be assigned to a parent element
    let el = deps.$document[0].querySelector(inParentNode + ` ${pos} .focusable`);
    if (!el) {
      // .focusable-first may be assigned to the focus element
      el = deps.$document[0].querySelector(inParentNode + ` ${pos}.focusable`);
    }
    if (!el) {
      el = deps.$document[0].querySelector(inParentNode + ' .focusable');
    }
    return el;
  }

  /**
   * @ngdoc
   * @name app.navigationService#focusEl
   * @methodOf app.navigationService
   * @example
   * ``` js
   * navigationService.focusEl('.class-name');
   * navigationService.focusEl('.class-name', 'first');
   * navigationService.focusEl('.class-name', 5);
   * ```
   * @param {string} inParentNode parent element containing the 'focusable' elements.
   * @param {(string|number)=} index  The position of the element. Values: first, last. Defaults to 'first'
   * @description
   * Set focus to an element belonging to the specified parent container.
   * It calls {@link api.navigationService#getFocusableEl getFocusableEl}
   * method with the passed in params.
   * if the element returned by the called function exists, it gives it focus.
   **/

  focusEl(inParentNode, atIndex) {
    const el = this.getFocusableEl(inParentNode, atIndex);
    if (el) {
      el.focus();
    }
  }

  /**
   * @ngdoc
   * @name app.navigationService#focusElAsync
   * @methodOf app.navigationService
   * @example
   * ``` js
   * navigationService.focusElAsync('.class-name');
   * navigationService.focusElAsync('.class-name', 'first');
   * navigationService.focusElAsync('.class-name', 5);
   * ```
   * @param {string} inParentNode parent element containing the 'focusable' elements.
   * @param {(string|number)=} index  The position of the element. Values: first, last. Defaults to 'first'
   * @description
   * Async version of focusEl
   * if the element returned by the called function exists, it gives it focus.
   **/
  focusElAsync(inParentNode, atIndex) {
    deps.$timeout(() => {
      const el = this.getFocusableEl(inParentNode, atIndex);
      if (!el) {
        this.digestCounter++;

        if (this.digestCounter > 5) {
          this.digestCounter = 0;
        } else {
          this.focusElAsync(inParentNode, atIndex);
        }
      } else {
        this.digestCounter = 0;
        el.focus();
      }
    });
  }


  /**
   * @ngdoc
   * @name app.navigationService#processKeyAction
   * @methodOf app.navigationService
   * @example
   * ``` js
   * navigationService.processKeyAction('navbar, 'down', {item: {id: ''}})
   * ```
   * @param {string} source The source component.
   * @param {string} key The code for the event fired on the passed in component element.
   * @param {object} data The data to be passed in.
   * @description
   * Private method to direct focus flow between components.
   **/

  processKeyAction(source, key, data) {
    // currentState can be used to further customize component navigation for specific pages
    const currentState = deps.$state.current.name;
    switch (source) {
      case 'navbarSubMenu':
        if (key === KEY_UP) {
          this.focusEl('navbar .selected');
        } else if (key === KEY_DOWN) {
          this.focusEl('.main-content');
        }
        break;
      case 'navbar':
        if (key === KEY_DOWN) {
          // if below sections are selected we should send focus to live feed enlarge button
          // instead of story main-content
          const sections = ['weather', 'features', 'traffic'];
          if (data.item && _.includes(sections, data.item.id) && (currentState !== deps.states.closingsAndDelays)) {
            this.focusEl('live-feed-video');
          } else {
            this.focusEl('.main-content');
          }
        }
        break;
      case 'poll-list':
        if (key === KEY_UP) {
          this.focusEl('navbar');
        } else if (key === KEY_DOWN) {
          this.focusEl('.btngroup', 1);
        } else if (key === KEY_ENTER) {
          this.focusEl('.main-content');
        } else if (key === KEY_RIGHT) {
          this.focusEl('live-feed-video');
        }
        break;
      case 'photo-list':
        if (key === KEY_UP) {
          this.focusEl('navbar');
        } else if (key === KEY_LEFT) {
          this.focusEl('.left-navbar');
        }
        break;
      case 'photo-gallery':
        if (key === KEY_UP) {
          this.focusEl('navbar');
        } else if (key === KEY_LEFT) {
          this.focusEl('.left-aside-promo-banner');
        }
        break;
      case 'home.closings':
        if (key === KEY_UP) {
          this.focusEl('navbar .selected');
        } else if (key === KEY_DOWN) {
          if (data.pollResultVisible) {
            this.focusEl('.main-menu-btn');
          } else {
            this.focusEl('.main-content');
          }
        }
        break;
      case 'incident-list':
        if (key === KEY_UP) {
          this.focusEl('navbar .selected');
        } else if (key === KEY_LEFT) {
          this.focusEl('.left-navbar');
        }
        break;
      case 'camera-gallery':
        if (key === KEY_UP) {
          this.focusEl('navbar');
        } else if (key === KEY_LEFT) {
          this.focusEl('.left-aside-promo-banner');
        }
        break;
      case 'camera-list':
        if (key === KEY_UP) {
          this.focusEl('navbar .selected');
        } else if (key === KEY_LEFT) {
          this.focusEl('.left-navbar');
        }
        break;
      default:
        console.warn(`NavigationService: Navigation for source '${source}' not defined`);
    }
  }

  //-----------------------------------
  // Static
  //-----------------------------------
  static factory($document, $state, $timeout, topics, states) {
    return new NavigationService($document, $state, $timeout, topics, states);
  }
}

NavigationService.factory.$inject = [
  '$document',
  '$state',
  '$timeout',
  'topics',
  'states'
];

const Module = angular.module('navigationService', [
  'constants'
])
.factory('navigationService', NavigationService.factory);

export default Module;



/** WEBPACK FOOTER **
 ** ./client/app/services/navigation/navigation.js
 **/